<template>
  <div class="tiptap-editor">
    <slot name="header" />

    <editor-menu-bar
      v-if="editable && actions.length"
      v-slot="{ commands, isActive, getMarkAttrs }"
      :editor="editor"
    >
      <div class="menubar">
        <template v-for="editorOption in computedActions">
          <v-btn
            :key="editorOption.name"
            class="menubar-button"
            width="30px"
            text
            type="button"
            :input-value="
              isActive[editorOption.tiptapName](editorOption.config)
            "
            :class="{
              'is-active': isActive[editorOption.tiptapName](
                editorOption.config
              ),
            }"
            color="black"
            :title="editorOption.title"
            @click="
              (evt) => {
                commands[editorOption.tiptapName](editorOption.config)
              }
            "
            v-html="editorOption.icon"
          />
        </template>
        <v-btn
            v-if="shopLink"
            class="menubar-button"
            width="30px"
            text
            type="button"
            :class="{
              'is-active': isActive.link(),
            }"
            color="black"
            :title="undefined"
            @click="() => showLinkMenu(getMarkAttrs('link'))"
            
          >{{$t('textbookIssue.shopLink.label')}}</v-btn>
      </div>
    </editor-menu-bar>
    <main class="tiptap-editor__content">
      <editor-content
        :editor="editor"
        class="tiptap-editor__content--editor"
        :spellcheck="spellcheck"
      />

      <div class="tiptap-editor__appended-content">
        <slot name="content.append" />
      </div>
    </main>
    <footer class="tiptap-editor__footer">
      <slot name="footer" />
    </footer>

    <v-dialog
      v-if="shopLink"
      :value="shopLinkDialogOpen"
      persistent
      attach="body"
      max-width="400"
      @keydown.esc="shopLinkDialogOpen = false"
      @input="(val) => shopLinkDialogOpen = val"
    >
    <v-card class="px-2 py-2 shop-link-dialog">
      <close-button @close="shopLinkDialogOpen = false" />
      <h3>{{$t('textbookIssue.form.label.shopLinkTitle')}}</h3>
      <v-form
        @submit.prevent="insertShopLink"
      >
      <v-text-field
        v-model.trim="shopLinkIsbn"
        ref="shopIsbnInput"
        class="required"
        :label="$t('textbookIssue.form.label.shopLink')"
        :rules="isbnRules"
        color="white"
      />
      <v-btn
          large
          depressed
          type="submit"
          :disabled="!shopLinkIsbnValid"
          class="v-btn--text success--success"
        >
          {{ $t('save') }}
        </v-btn>
    </v-form></v-card></v-dialog>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import CloseButton from './CloseButton.vue'
import {
  Bold,
  Blockquote,
  BulletList,
  HardBreak,
  Heading,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Strike,
  Underline,
  Placeholder,
} from 'tiptap-extensions'

function isValidIsbn(isbn) {
  const digits = []
  const validDigits = '0123456789'.split("")
  const validCharacters = validDigits.concat(['-'])
  if((typeof isbn) !== 'string') {
    console.log('not a string', isbn)
    return false
  }
  const characters = isbn.split("")
  for(const char of characters) {
    if (!validCharacters.includes(char)) {
      console.log('invalid character', char, characters)
      return false
    }
    if (!validDigits.includes(char)) {
      continue
    }
    digits.push(Number(char))
  }
  if (digits.length === 10) {
    let index = 10
    let sum = 0
    for (const digit of digits) {
      sum += digit*index
      index--
    }
    console.log('10 digit sum', sum%11, sum, digits)
    return sum%11 === 0
  }
  if (digits.length === 13) {
    let index = 0
    let sum = 0
    for (const digit of digits) {
      sum += digit*(1 + (2*(index%2))) // alternate 1 and 3 factor
      index++
    }
    console.log('13 digit sum', sum%10, sum, digits)
    return sum%10 === 0
  }
  console.log('invalid length', digits)
  return false
}

export default {
  name: 'TiptapEditor',
  components: {
    EditorContent,
    EditorMenuBar,
    CloseButton
  },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: null,
    },
    shopLink: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
    editor: null,
    extensions: {
      bold: {
        tiptapName: 'bold',
        icon: '<b>B<b>',
      },
      italic: {
        tiptapName: 'italic',
        icon: '<i>I<i>',
      },
      underline: {
        tiptapName: 'underline',
        icon: '<u>U<u>',
      },
      blockquote: {
        tiptapName: 'blockquote',
        icon: '“ ”',
      },
      orderedList: {
        tiptapName: 'ordered_list',
        icon: '#',
      },
      bulletList: {
        tiptapName: 'bullet_list',
        icon: '•',
      },
      strike: {
        tiptapName: 'strike',
        icon: '<strike>S</strike>',
      },
      heading1: {
        tiptapName: 'heading',
        icon: '<b>H1</b>',
        config: { level: 1 },
      },
      heading2: {
        tiptapName: 'heading',
        icon: '<b>H2</b>',
        config: { level: 2 },
      },
    },
    spellcheck: false,
    shopLinkDialogOpen: false,
    shopLinkIsbn: '',
    isbnRules: [
        (v) =>
          (!!v && isValidIsbn(v)) || this.$t('textbookIssue.form.error.shopLinkIsbnInvalid'),
      ],
    }
  },
  methods: {
    insertShopLink() {
      const linkPlaceholder = `##ISBN:${this.shopLinkIsbn}##`
      this.setLinkUrl(this.editor.commands.link, { href: linkPlaceholder})
      this.hideLinkMenu()
      // this.editor.view.dispatch(this.editor.state.tr.insertText(text, this.editor.selection.$from, this.editor.selection.$to))
      // this.shopLinkDialogOpen = false
    },
    showLinkMenu (attrs) {
      const transformPlaceholder = (text) => {
        if (!text) {
          return text
        }
        return text.replace('##ISBN:', '').replace('##', '')
      }
      this.shopLinkIsbn = transformPlaceholder(attrs.href)
      this.shopLinkDialogOpen = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.shopIsbnInput.focus()
        }, 50)
      })
    },
    hideLinkMenu () {
      this.shopLinkIsbn = null
      this.shopLinkDialogOpen = false
    },
    setLinkUrl (command, {href}) {
      command({ href })
      this.hideLinkMenu()
    }
  },
  computed: {
    computedActions() {
      const extensions = this.extensions
      return this.actions
        .filter(
          (option) =>
            (option.name && Object.keys(extensions).includes(option.name)) ||
            Object.keys(this.extensions).includes(option)
        )
        .map((action) =>
          action.name
            ? {
                ...extensions[action.name],
                ...action,
              }
            : {
                ...extensions[action],
                name: action,
              }
        )
    },
    shopLinkIsbnValid() {
      if (!this.shopLinkIsbn) {
        return false
      }
      return isValidIsbn(this.shopLinkIsbn)
    }
  },
  watch: {
    editable: function (newVal) {
      this.editor.setOptions({
        editable: newVal,
      })

      if (this.editor.getHTML() !== this.value) {
        this.editor.setContent(this.value)
      }
    },
    value: function (newVal) {
      if (newVal !== this.editor.getHTML()) {
        this.editor.setContent(newVal)
      }
    },
    shopLinkDialogOpen(newVal) {
      if (!newVal) {
        this.shopLinkIsbn = ''
      }
    }
  },
  created() {
    if (window.spellcheck) {
      this.spellcheck = true
    }

    this.editor = new Editor({
      autoFocus: false,
      editable: this.editable,
      extensions: [
        new Bold(),
        new Blockquote(),
        new Italic(),
        new Underline(),
        new Strike(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new ListItem(),
        new BulletList(),
        new OrderedList(),
        new Placeholder({ emptyNodeText: this.placeholder }),
        new Link({ target: '_blank'}),
      ],
      content: this.value,
      onUpdate: ({ getHTML }) => {
        this.$emit('input', getHTML())
        this.$emit('change', getHTML())
      },
    })
  },
  mounted() {
    if (this.height) {
      const el = this.$el.querySelector(
        '.tiptap-editor tiptap-editor__content--editor .ProseMirror'
      )
      if (el !== null) {
        el.style.height = this.height
      }
    }
    setTimeout(() => this.editor.focus(), 50)
  },
  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>
<style lang="scss">
@import 'Editor';
.tiptap-editor {
  .v-btn:not(.v-btn--round) {
    min-width: none !important;
  }
}
.shop-link-dialog {
  position: relative;
  padding: 10px 15px;
}
</style>
